import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import { Hash, Lock, Video, ArrowRight } from "lucide-react";
import { isMobile } from "react-device-detect";

import { colors } from "../../Assets/styles/colors";
import { ReactComponent as CloseBtn } from "../../Assets/iconsv2/generic/closeBtn.svg";
import Button from "../GenericComponents/Button";
import IconInputField from "../GenericComponents/IconInputField";
import SpinnerComponent from "../GenericComponents/SpinnerComponent";
import OnboardMeetingAnnonymousUser from "./OnboardMeetingAnnonymousUser";
import { useMeetingsService } from "../../Context/MeetingContext";
import { useLinksquaredService } from "../../Context/LinksquaredContext";
import { useAuthService } from "../../Context/AuthContext";
import {
  encodeJoinByCodeURL,
  encodeMeetingURL,
} from "../../Helpers/deepLinkHelper";
import { useNotify } from "../../Helpers/Notifications";
import LocalStorage from "../../Helpers/LocalStorage";
import {
  MEETING_DISPLAY_ID_LENGTH,
  MEETING_PASSCODE_LENGTH,
} from "../../Constants/constantValues";
import { meetingJoinErrorMessage } from "../../Helpers/meetingJoinErrorHelper";
import {
  formatMeetingDisplayId, normalizeDigits,
} from "../../Helpers/meetingHelper";

const DISPLAY_ID_PLACEHOLDER = formatMeetingDisplayId(
  "0".repeat(MEETING_DISPLAY_ID_LENGTH)
);
const PASSCODE_PLACEHOLDER = "0".repeat(MEETING_PASSCODE_LENGTH);

const JoinMeetingByCodeForm = ({
  onClose,
  onJoined,
  isAuthResolving = false,
  initialCredentials = null,
  showDesktopOption = false,
}) => {
  const { t } = useTranslate();
  const { notifyError } = useNotify();
  const { joinByCode, joinByCodeUnauthenticated, validateJoinCode } = useMeetingsService();
  const { setDeepLink } = useLinksquaredService();
  const { user, handleLoginAnonUser } = useAuthService();

  const [displayId, setDisplayId] = useState("");
  const [passcode, setPasscode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAnonymousForm, setShowAnonymousForm] = useState(false);

  const hasCredentials = !!displayId.trim() && !!passcode.trim();
  const canOfferDesktop = showDesktopOption && !window.electron && !isMobile;

  const handleJoinError = (error) => {
    notifyError(meetingJoinErrorMessage(error, t));
  };

  const handleJoinSuccess = (meeting) => {
    setIsSubmitting(false);
    setDeepLink(
      encodeMeetingURL(meeting.id, meeting.title, meeting.description)
    );
    onJoined();
  };

  const handleJoin = (id, code) => {
    if (isSubmitting || isAuthResolving || !id.trim() || !code.trim()) {
      return;
    }

    setIsSubmitting(true);

    if (!user) {
      validateJoinCode(
        id.trim(),
        code.trim(),
        () => {
          setIsSubmitting(false);
          setShowAnonymousForm(true);
        },
        (error) => {
          setIsSubmitting(false);
          handleJoinError(error);
        }
      );
      return;
    }

    joinByCode(
      id.trim(),
      code.trim(),
      (response) => {
        handleJoinSuccess(response.data.meeting);
      },
      (error) => {
        setIsSubmitting(false);
        handleJoinError(error);
      }
    );
  };

  const handleAnonymousJoin = (meetingId, name, callLanguage, language) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    joinByCodeUnauthenticated(
      displayId.trim(),
      passcode.trim(),
      name,
      callLanguage,
      language,
      (response) => {
        handleLoginAnonUser(response.data.user);
        LocalStorage.setAuthenthicationToken(response.data.token);
        LocalStorage.setRefreshToken(response.data.refresh_token);
        LocalStorage.setSSOFlag(true);
        setShowAnonymousForm(false);
        handleJoinSuccess(response.data.meeting);
      },
      (error) => {
        setIsSubmitting(false);
        handleJoinError(error);
      }
    );
  };

  useEffect(() => {
    if (!initialCredentials || isAuthResolving) {
      return;
    }

    const id = normalizeDigits(initialCredentials.displayId, MEETING_DISPLAY_ID_LENGTH);
    const code = normalizeDigits(initialCredentials.passcode, MEETING_PASSCODE_LENGTH);

    setDisplayId(id);
    setPasscode(code);
    handleJoin(id, code);
  }, [initialCredentials, isAuthResolving]);

  if (showAnonymousForm) {
    return (
      <SpinnerComponent isLoading={isSubmitting}>
        <OnboardMeetingAnnonymousUser
          meeting={{ id: displayId }}
          requestToJoin={handleAnonymousJoin}
          handleMeetingHasEnded={() => setShowAnonymousForm(false)}
        />
      </SpinnerComponent>
    );
  }

  return (
    <Container>
      {onClose && (
        <Header>
          <TitleWrapper>
            <Title>{t("meetings.join_with_meeting_id")}</Title>
          </TitleWrapper>
          <CloseButton onClick={onClose}>
            <CloseBtn />
          </CloseButton>
        </Header>
      )}
      <CardInputsWrapper>
        <CenteredCard>
          <IconBadge>
            <Video size={28} />
          </IconBadge>
          <Headline>{t("meetings.join_a_meeting")}</Headline>
          <Subtitle>{t("meetings.join_a_meeting_subtitle")}</Subtitle>
        </CenteredCard>
        <FormCard>
          <Group>
            <IconInputField
              label={t("meetings.meeting_id")}
              icon={<Hash size={18} />}
              size="lg"
              borderRadius="12px"
              value={displayId}
              placeholder={DISPLAY_ID_PLACEHOLDER}
              onChange={(e) =>
                setDisplayId(normalizeDigits(e.currentTarget.value, MEETING_DISPLAY_ID_LENGTH))
              }
              name="joinMeetingDisplayId"
            />
            <IconInputField
              label={t("meetings.passcode")}
              icon={<Lock size={18} />}
              size="lg"
              borderRadius="12px"
              value={passcode}
              placeholder={PASSCODE_PLACEHOLDER}
              onChange={(e) =>
                setPasscode(normalizeDigits(e.currentTarget.value, MEETING_PASSCODE_LENGTH))
              }
              name="joinMeetingPasscode"
            />
          </Group>
          <Group>
            <Button
              size="lg"
              fullWidth
              borderRadius="12px"
              iconRight={<ArrowRight size={18} />}
              disabled={!hasCredentials || isSubmitting || isAuthResolving}
              onClick={() => handleJoin(displayId, passcode)}
            >
              {t("meetings.join_meeting")}
            </Button>
          </Group>
          {canOfferDesktop && (
            <DesktopOption>
              <DesktopOptionText>
                {t("meetings.have_desktop_app")}{" "}
                <DesktopLink
                  href={hasCredentials ? encodeJoinByCodeURL(displayId, passcode) : undefined}
                >
                  {t("meetings.open_in_desktop_app")}
                </DesktopLink>
              </DesktopOptionText>
              <DesktopOptionText>
                {t("login.dont_have_app")}{" "}
                <DesktopLink href="https://emotii.ai" target="_blank" rel="noreferrer">
                  {t("login.download_it_now")}
                </DesktopLink>
              </DesktopOptionText>
            </DesktopOption>
          )}
        </FormCard>
      </CardInputsWrapper>
    </Container>
  );
};

export default JoinMeetingByCodeForm;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 19px 16px;
  align-items: center;
  background: ${({ theme }) => theme.chat.conversationBackground};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.p`
  text-transform: capitalize;
  font-weight: 700;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.04px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    height: 26px;
    width: 26px;
    color: ${({ theme }) => theme.menu.selectedOpionBackground};
    stroke: ${({ theme }) => theme.sidebar.borderColor};
  }
`;

const CardInputsWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: safe center;
  padding: 25px 21px;
  gap: 40px;
  overflow: auto;
  max-width: 580px;
  width: 100%;
  margin: 0 auto;
`;

const CenteredCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const IconBadge = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: linear-gradient(135deg, ${({ theme }) => theme.primaryButton.borderColor}, ${colors.darkPurple});
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const Headline = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 26px;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 15px;
  line-height: 21px;
`;

const FormCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding: 40px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme }) => theme.chat.conversationBackground};
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const DesktopOption = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
`;

const DesktopOptionText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 13px;
  line-height: 18px;
`;

const DesktopLink = styled.a`
  color: ${({ theme }) => theme.primaryButton.borderColor};
  font-weight: 600;

  &:not([href]) {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
